import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import ToggleButton from 'react-bootstrap/ToggleButton'
import { upperFirstOnly } from 'utils/format'
import classNames from 'classnames/bind'
import { useTranslations } from 'config/locale/hooks/requireContext'
import { ReactComponent as VolumeIcon } from 'images/analytics-report/volume.svg'
import { ReactComponent as MarginIcon } from 'images/analytics-report/margin.svg'
import { ReactComponent as CostIcon } from 'images/analytics-report/costs.svg'
import { ReactComponent as TransactionIcon } from 'images/analytics-report/transactions.svg'
import { ReactComponent as FillUpIcon } from 'images/analytics-report/fill-up.svg'
import { Dropdown } from 'components/common/dropdown'
import { MultiSelect } from 'components/common/multi-select'
import styles from './Metrics.module.scss'
import { metricVar } from 'config/apollo/analytics-report/variables'

const isMetricAnObject = (value) => value && typeof value === 'object' && !Array.isArray(value)

const isMultiSelectMode = (activeTab) => {
  const currentMetric = metricVar()
  const stored = currentMetric[activeTab]
  return isMetricAnObject(stored)
}

const EMPTY_MULTI_METRIC = {
  volume: [],
  margin: [],
  profit: [],
  cost: [],
}

const getMultiMetricForTab = (activeTab) => {
  const currentMetric = metricVar()
  const stored = currentMetric[activeTab]

  if (!isMetricAnObject(stored)) return EMPTY_MULTI_METRIC

  return {
    volume: stored.volume || [],
    margin: stored.margin || [],
    profit: stored.profit || [],
    cost: stored.cost || [],
  }
}

const cx = classNames.bind(styles)

const Metrics = ({ activeTab }) => {
  const { t } = useTranslation(['translations'])
  const translations = useTranslations(i18n.language)
  const SmallProfitIcon = translations?.smallProfitIcon

  const [metric, setMetric] = useState(metricVar)
  const usesMultiSelect = isMultiSelectMode(activeTab)
  const [metricMulti, setMetricMulti] = useState(() =>
    usesMultiSelect ? getMultiMetricForTab(activeTab) : EMPTY_MULTI_METRIC
  )

  useEffect(() => {
    setMetricMulti(usesMultiSelect ? getMultiMetricForTab(activeTab) : EMPTY_MULTI_METRIC)
  }, [activeTab, usesMultiSelect])

  const setMetricVar = (selectedMetric) =>{
    metricVar({
      ...metricVar(),
      [activeTab]: selectedMetric
    })
  }

  const setSelectedMetric = (selectedMetric) => {
    setMetric((prevState) => ({
      ...prevState,
      [activeTab]: selectedMetric
    }))
  }

  const setSelectedMetricMulti = (category, selectedItemValues) => {
    setMetricMulti((prevState) => ({
      ...prevState,
      [category]: selectedItemValues
    }))
    const currentMetric = metricVar()
    const existing = isMetricAnObject(currentMetric[activeTab]) ? currentMetric[activeTab] : {}
    metricVar({
      ...currentMetric,
      [activeTab]: {
        ...existing,
        [category]: selectedItemValues
      }
    })
  }

  const competitorAnalysisMetricItems = [
    {
      value: 'comparison_metric',
      label: 'Comparison Metric',
      items: [
        { item: 'total_fuel_volume_sales', displayItem: upperFirstOnly(t('widgets.analytics.totalVolume'))},
        { item: 'retail_fuel_volume_sales_for_computing_margin', displayItem: upperFirstOnly(t('widgets.analytics.retailVolume'))},
        { item: 'bunkered_fuel_volume_sales', displayItem: upperFirstOnly(t('widgets.analytics.bunkeredVolume'))},
        { item: 'consumer_fuel_volume_sales', displayItem: upperFirstOnly(t('widgets.analytics.consumerVolume'))},
        { item: 'fuel_card_fuel_volume_sales', displayItem: upperFirstOnly(t('widgets.analytics.fuelCardVolume'))},
        { item: 'retail_gross_sales_ex_vat_for_computing_margin', displayItem: upperFirstOnly(t('widgets.analytics.grossSales'))},
        { item: 'num_retail_transactions', displayItem: upperFirstOnly(t('widgets.analytics.retailTransactions'))},
        { item: 'avg_retail_fill_up', displayItem: upperFirstOnly(t('widgets.analytics.retailFillUp'))},
      ]
    }
  ]

  const metricItems = [
    {
      value: 'volume',
      label: upperFirstOnly(t('widgets.analytics.volume')),
      items: [
        { item: 'total_fuel_volume_sales', displayItem: upperFirstOnly(t('widgets.analytics.totalVolume')) },
        { item: 'retail_fuel_volume_sales_for_computing_margin', displayItem: upperFirstOnly(t('widgets.analytics.retailVolume')) },
        { item: 'bunkered_fuel_volume_sales', displayItem: upperFirstOnly(t('widgets.analytics.bunkeredVolume')) },
        { item: 'consumer_fuel_volume_sales', displayItem: upperFirstOnly(t('widgets.analytics.consumerVolume')) },
        { item: 'fuel_card_fuel_volume_sales', displayItem: upperFirstOnly(t('widgets.analytics.fuelCardVolume')) },
      ],
      Icon: VolumeIcon,
    },
    {
      value: 'margin',
      label: upperFirstOnly(t('widgets.analytics.margin')),
      items: [
        { item: 'gross_margin', displayItem: upperFirstOnly(t('widgets.analytics.grossMargin')) },
        { item: 'net_margin', displayItem: upperFirstOnly(t('widgets.analytics.netMargin')) },
      ],
      Icon: MarginIcon,
    },
    {
      value: 'profit',
      label: t('widgets.analytics.salesProfit'),
      items: [
        { item: 'retail_gross_sales_ex_vat_for_computing_margin', displayItem: upperFirstOnly(t('widgets.analytics.grossSales')) },
        { item: 'gross_profit', displayItem: upperFirstOnly(t('widgets.analytics.grossProfit')) },
        { item: 'net_profit', displayItem: upperFirstOnly(t('widgets.analytics.netProfit')) },
        { item: 'business_profit', displayItem: upperFirstOnly(t('widgets.analytics.businessProfit')) },
      ],
      Icon: SmallProfitIcon,
    },
    {
      value: 'cost',
      label: t('widgets.analytics.costFees'),
      items: [
        { item: 'bunkering_commissions', displayItem: upperFirstOnly(t('widgets.analytics.bunkeringComissions')) },
        { item: 'cost_price', displayItem: upperFirstOnly(t('widgets.analytics.replacementCost')) },
        { item: 'total_fees', displayItem: upperFirstOnly(t('widgets.analytics.totalFees')) },
        { item: 'retail_blended_cost', displayItem: upperFirstOnly(t('widgets.analytics.blendedCostSold')) },
      ],
      Icon: CostIcon,
    },
  ]

  const tabs = [
    { value: 'num_retail_transactions', label: upperFirstOnly(t('widgets.analytics.retailTransactions')), Icon: TransactionIcon },
    { value: 'avg_retail_fill_up', label: upperFirstOnly(t('widgets.analytics.retailFillUp')), Icon: FillUpIcon },
  ]

  const handleItemClick = (event) => {
    const selectedList = activeTab === 'COMPETITOR_ANALYSIS' ? competitorAnalysisMetricItems : metricItems
    const target = event.target.querySelector('span')
    const items = selectedList.map((metricItem) => metricItem.items.find((item) => item.displayItem === target?.textContent))
    const item = items.filter((n) => n)

    if (item[0]) {
      setSelectedMetric(item[0].item)
      setMetricVar(item[0].item)
    }
  }

  const handleTabClick = (event) => {
    const target = event.currentTarget.value

    if (usesMultiSelect) {
      const currentMetric = metricVar()
      const existing = isMetricAnObject(currentMetric[activeTab]) ? currentMetric[activeTab] : {}
      const updated = {
        ...existing,
        [target]: !existing[target],
      }
      setSelectedMetric(updated)
      metricVar({
        ...currentMetric,
        [activeTab]: updated
      })
    } else {
      setSelectedMetric(target)
      setMetricVar(target)
    }
  }

  const parseSelectedItem = () => {
    const selectedList = activeTab === 'COMPETITOR_ANALYSIS' ? competitorAnalysisMetricItems : metricItems
    return selectedList.map((metricItem) => {
      const foundItem = metricItem.items.find((item1) => item1.item === metric[activeTab])

      if (foundItem) return metric[activeTab]
      else return metricItem.label
    })
  }

  const selectedItem = parseSelectedItem()

  const itemListToDisplay = activeTab === 'COMPETITOR_ANALYSIS' ? competitorAnalysisMetricItems : metricItems

  const setIcon = (itemName) => {
    if (itemName === 'avg_retail_fill_up') return FillUpIcon
    else if (itemName === 'num_retail_transactions') return TransactionIcon
    else if (itemName === 'retail_gross_sales_ex_vat_for_computing_margin') return SmallProfitIcon
    else return VolumeIcon
  }

  const competitorAnalysisMetrics = () =>{
    const SelectedIcon = setIcon(selectedItem[0])

    return(
      <>
        {itemListToDisplay.map((currentDropdown, i) => (
          <Dropdown
            customClass={cx('competitor-analysis-dropdown')}
            key={`${i}_metric_dropdown_item_${currentDropdown.label}`}
            selectedItem={selectedItem[i]}
            items={currentDropdown.items}
            leftAccessory={SelectedIcon && <SelectedIcon className={cx('icon')} />}
            onItemClick={handleItemClick}
            selectedDropdownHighlight={true}
            dataTestId={currentDropdown.label.toLowerCase().replace(/\//g, '-')}
          />
        ))}
      </>
    )}

  if (activeTab === 'COMPETITOR_ANALYSIS') {
    return competitorAnalysisMetrics()
  }

  return (
    <div className={cx('select-boxes')}>
      {usesMultiSelect ? (
        // Multi-select mode
        <>
          {metricItems.map((currentMetric, i) => {
            const category = currentMetric.value
            const items = currentMetric.items.map(item => item.displayItem)
            const selectedItemValues = metricMulti[category] || []
            const selectedDisplayItems = currentMetric.items
              .filter(item => selectedItemValues.includes(item.item))
              .map(item => item.displayItem)
            return (
              <MultiSelect
                key={`${i}_metric_multiselect_${currentMetric.label}`}
                placeholder={currentMetric.label}
                allItemsText={upperFirstOnly(t('widgets.analytics.allSelected'))}
                items={items}
                value={selectedDisplayItems}
                minSelected={0}
                onValueSelect={(selectedDisplayItems) => {
                  const itemValues = selectedDisplayItems.map(displayItem => {
                    const found = currentMetric.items.find(item => item.displayItem === displayItem)
                    return found ? found.item : null
                  }).filter(Boolean)
                  setSelectedMetricMulti(category, itemValues)
                }}
                onDropdownClick={() => {}}
                leftAccessory={currentMetric.Icon && <currentMetric.Icon className={cx('icon')} />}
                dataTestId={currentMetric.label.toLowerCase().replace(/\//g, '-')}
              />
            )
          })}
          {tabs.map((currentTab) => {
            const isChecked = isMetricAnObject(metric[activeTab])
              ? metric[activeTab][currentTab.value] === true
              : false
            return (
              <ToggleButton
                key={currentTab.value}
                type="checkbox"
                name={`tabs-${currentTab.value}`}
                className={cx('tab', { disabled: currentTab.disabled })}
                value={currentTab.value}
                disabled={currentTab.disabled}
                checked={isChecked}
                onChange={handleTabClick}
                data-testid={currentTab.value}
              >
                {currentTab.Icon && <currentTab.Icon />}
                <span>{currentTab.label}</span>
              </ToggleButton>
            )
          })}
        </>
      ) : (
        // Single-select mode (regular dropdowns)
        <>
          {metricItems.map((currentDropdown, i) => (
            <Dropdown
              key={`${i}_metric_dropdown_item_${currentDropdown.label}`}
              selectedItem={selectedItem[i]}
              items={currentDropdown.items}
              leftAccessory={currentDropdown.Icon && <currentDropdown.Icon className={cx('icon')} />}
              onItemClick={handleItemClick}
              selectedDropdownHighlight={true}
              dataTestId={currentDropdown.label.toLowerCase().replace(/\//g, '-')}
            />
          ))}
          {tabs.map((currentTab) => (
            <ToggleButton
              key={currentTab.value}
              type="radio"
              name="tabs"
              className={cx('tab', { disabled: currentTab.disabled })}
              value={currentTab.value}
              disabled={currentTab.disabled}
              checked={metric[activeTab] === currentTab.value}
              onChange={handleTabClick}
              data-testid={currentTab.value}
            >
              {currentTab.Icon && <currentTab.Icon />}
              <span>{currentTab.label}</span>
            </ToggleButton>
          ))}
        </>
      )}
    </div>
  )
}

Metrics.propTypes = {
  activeTab: PropTypes.string,
}

export { Metrics }
